import React, { useState, useEffect } from "react";
import LeftNav from "../common/leftnav";
import { useUser } from "../../context/UserContext";
import ConfirmationModal from "./components/ConfirmationModal";
import authService from "../../services/authService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TestBox = () => {
  const { token } = useUser();
  const [customPrompts, setCustomPrompts] = useState({
    tools: [],
    dynamic_prompt: "You are a helpful assistant that can use the function when appropriate."
  });
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [commonDynamicPrompt, setCommonDynamicPrompt] = useState(
    "You are a helpful assistant that can use the function when appropriate."
  );
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  const [functionForm, setFunctionForm] = useState({
    name: "",
    description: "",
    url: "",
    request_type: "GET",
    // timeout: 60000,
    parameters: "{}"
  });

  useEffect(() => {
    const fetchCustomPrompts = async () => {
      if (!token) {
        console.error("No authentication token available");
        return;
      }
      
      try {
        const data = await authService.fetchCustomPrompts(token);
        
        // Handle data as an object
        if (data && typeof data === 'object') {

          const {_id, user, __v, ...rest} = data
          setCustomPrompts(rest);
          
          // Set common dynamic prompt if available
          if (rest.dynamic_prompt) {
            setCommonDynamicPrompt(rest.dynamic_prompt);
          }
          
          // Ensure tools is always an array
          if (!rest.tools || !Array.isArray(rest.tools)) {
            setCustomPrompts(prev => ({
              ...prev,
              tools: []
            }));
          }
        }
      } catch (err) {
        setError("Failed to fetch custom prompts");
        console.error(err);

        // Show error notification with the backend error message
        const errorMessage = err.response?.data?.message || "Failed to fetch custom prompts";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCustomPrompts();
  }, [token]);

  const handleEdit = (functionTool, index) => {
    if (functionTool) {
      setIsEditing(true);
      setSelectedFunction({
        index: index
      });
      
      setFunctionForm({
        name: functionTool.name || "",
        description: functionTool.description || "",
        url: functionTool.webhook_url || "",
        request_type: functionTool.request_type || "GET",
        parameters: JSON.stringify(functionTool.parameters || {}, null, 2)
      });
      
      setShowModal(true);
    }
  };

  const handleAdd = () => {
    setIsEditing(false);
    setSelectedFunction(null);
    setFunctionForm({
      name: "",
      description: "",
      url: "",
      request_type: "POST",
      // timeout: 60000,
      parameters: JSON.stringify({
        type: "object",
        properties: {
          example_param: {
            type: "string",
            description: "Example parameter description"
          }
        },
        required: ["example_param"]
      }, null, 2)
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFunctionForm({
      ...functionForm,
      [name]: value
    });
  };

  const handleDynamicPromptChange = (e) => {
    setCommonDynamicPrompt(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const functionData = {
        type: "function",
        name: functionForm.name.includes(' ') ? functionForm.name.split(' ').join('_').toLowerCase() : functionForm.name.toLowerCase(), // Create function name from prompt name
        description: functionForm.description,
        webhook_url: functionForm.url,
        request_type: functionForm.request_type,
        // timeout: parseInt(functionForm.timeout),
        parameters: functionForm.parameters.trim() === "" ? {} : JSON.parse(functionForm.parameters) // Handle empty parameters
      };
      
      let updatedTools = [...(customPrompts.tools || [])];
      
      if (isEditing && selectedFunction) {
        // Update existing function
        updatedTools[selectedFunction.index] = functionData;
      } else {
        // Add new function
        updatedTools.push(functionData);
      }
      
      const promptData = {
        tools: updatedTools,
        dynamic_prompt: commonDynamicPrompt // Use the common dynamic prompt
      };
      
      // Update the customPrompt with the updated tools array
      await authService.updateCustomPrompt(token, promptData);
      
      toast.success(isEditing ? "Function updated successfully" : "Function created successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Update state
      setCustomPrompts({
        ...customPrompts,
        tools: updatedTools
      });
      
      // Close the modal
      handleCancel();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 
        (isEditing ? "Failed to update function" : "Failed to create function");
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleSaveDynamicPrompt = async () => {
    try {
      const updatedPrompt = {
        ...customPrompts,
        dynamic_prompt: commonDynamicPrompt
      };
      
      await authService.updateCustomPrompt(token, updatedPrompt);
      
      // Update state
      setCustomPrompts({
        ...customPrompts,
        dynamic_prompt: commonDynamicPrompt
      });
      
      toast.success("Dynamic prompt updated for all functions", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || "Failed to update dynamic prompt";
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedFunction(null);
    setFunctionForm({
      name: "",
      description: "",
      url: "",
      // timeout: 60000,
      parameters: "{}"
    });
  };
  
   const handleDelete = (index) => {
    setDeleteIndex(index); // Set the index of the function to delete
    setIsDeleteModalOpen(true); // Open the confirmation modal
  };

  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      try {
        const updatedTools = [...customPrompts.tools];
        updatedTools.splice(deleteIndex, 1);

        const updatedPrompt = {
          ...customPrompts,
          tools: updatedTools,
        };

        await authService.updateCustomPrompt(token, updatedPrompt);

        toast.success("Function deleted successfully", {
          position: "top-right",
          autoClose: 3000,
        });

        setCustomPrompts({
          ...customPrompts,
          tools: updatedTools,
        });
      } catch (err) {
        console.error(err);
        const errorMessage = err.response?.data?.message || "Failed to delete function";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
        });
      } finally {
        setIsDeleteModalOpen(false); // Close the modal
        setDeleteIndex(null); // Reset the delete index
      }
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false); // Close the modal
    setDeleteIndex(null); // Reset the delete index
  };

  // Loading skeleton
  const FunctionsSkeleton = () => (
    <div>
      {[1, 2, 3].map(i => (
        <div key={i} className="list-group-item d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="placeholder-glow me-3" style={{ width: "24px", height: "24px" }}>
              <span className="placeholder col-12 h-100"></span>
            </div>
            <span className="placeholder-glow">
              <span className="placeholder col-8"></span>
            </span>
          </div>
          <div>
            <button className="btn btn-sm btn-outline-secondary me-2 disabled">
              <span className="placeholder col-12" style={{ width: "20px" }}></span>
            </button>
            <button className="btn btn-sm btn-outline-secondary disabled">
              <span className="placeholder col-12" style={{ width: "20px" }}></span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // Function to get the function name display
  const getFunctionDisplayName = (func) => {
    // Try to get a meaningful name in order of preference
    return func.name || func.id || "Unnamed Function";
  };

  return (
    <div className="d-flex">
      <LeftNav />
      <div className="dashboard-content flex-grow-1 p-4 bg-light">
        <div className="mb-4">
          <h2 className="fs-five-title fw-bold ms-2">Functions</h2>
          <p className="text-muted ms-2">Enable your agent with capabilities such as calendar bookings, call termination, etc.</p>
          
          {/* Common Dynamic Prompt section */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Dynamic Prompt</h5>
            </div>
            <div className="card-body">
              <div className="form-text mb-2">Instructions for how the assistant should use functions.</div>
              <textarea
                className="form-control mb-3"
                id="commonDynamicPrompt"
                name="commonDynamicPrompt"
                value={commonDynamicPrompt}
                onChange={handleDynamicPromptChange}
                rows="6"
              ></textarea>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveDynamicPrompt}
              >
                Save Dynamic Prompt
              </button>
            </div>
          </div>
          
          <div className="d-flex justify-content-end mb-3">
            <button 
              className="btn btn-primary" 
              onClick={handleAdd}
            >
              <span className="me-2">+</span> Add
            </button>
          </div>
          
          {/* Show Skeleton Loader while loading */}
          {loading ? (
            <FunctionsSkeleton />
          ) : (
            <div className="list-group">
              {!customPrompts.tools || customPrompts.tools.length === 0 ? (
                <div className="alert alert-info">
                  No functions found. Click the Add button to create a new function.
                </div>
              ) : (
                customPrompts.tools.map((tool, index) => {
                  // Skip non-function tools
                  if (!tool || tool.type !== "function") {
                    return null;
                  }
                  
                  return (
                    <div 
                      className="list-group-item d-flex justify-content-between align-items-center" 
                      key={index}
                    >
                      <div className="d-flex align-items-center">
                        <img 
                          src="/assets/four-pieces.svg" 
                          alt={getFunctionDisplayName(tool)} 
                          className="me-3"
                          style={{ width: "24px", height: "24px" }}
                        />
                        <span>{getFunctionDisplayName(tool)}</span>
                      </div>
                      <div>
                        <button 
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => handleEdit(tool, index)}
                        >
                          <img src="/assets/edit-2.svg" alt="Edit" className="icon-sm" />
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleDelete(index)}
                        >
                          <img
                            src="/assets/trash-bin-minimalistic-small.svg"
                            alt="Delete"
                            className="icon-sm"
                            style={{ width: "24px", height: "24px" }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this function? This action cannot be undone."
      />
      
      {/* Modal for Add/Edit Function */}
      {showModal && (
        <div className="modal" style={{display: "block", backgroundColor: "rgba(0,0,0,0.5)"}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditing ? "Edit Function" : "Custom Function"}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancel}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Function Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={functionForm.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={functionForm.description}
                      onChange={handleChange}
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="url" className="form-label">Your URL</label>
                    <div className="input-group">
                      {/* Request Type Dropdown */}
                      <select
                        className="form-select"
                        id="request_type"
                        name="request_type"
                        value={functionForm.request_type}
                        onChange={handleChange}
                        style={{ maxWidth: "100px" }} // Adjust width as needed
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        {/* Add more options if needed */}
                      </select>
                      {/* URL Input */}
                      <input
                        type="url"
                        className="form-control"
                        id="url"
                        name="url"
                        value={functionForm.url}
                        onChange={handleChange}
                        required
                        placeholder="Enter your URL"
                      />
                    </div>
                  </div>
                  
                  {/* <div className="mb-3">
                    <label htmlFor="timeout" className="form-label">API Timeout (Optional)</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        id="timeout"
                        name="timeout"
                        value={functionForm.timeout}
                        onChange={handleChange}
                      />
                      <span className="input-group-text">milliseconds</span>
                    </div>
                  </div> */}
                  
                  <div className="mb-3">
                    <label htmlFor="parameters" className="form-label">Parameters (Optional)</label>
                    <div className="form-text mb-2">JSON schema that defines the format in which the LLM will return. Please refer to the docs.</div>
                    <textarea
                      className="form-control bg-dark text-light"
                      id="parameters"
                      name="parameters"
                      value={functionForm.parameters}
                      onChange={handleChange}
                      rows="10"
                      style={{fontFamily: "monospace"}}
                    ></textarea>
                  </div>
                  
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary" 
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {isEditing ? "Save Changes" : "Create Function"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestBox;